import { useState, useEffect } from "react"
import { useAuth } from "@/lib/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { GoogleLoginButton } from "@/components/auth/google/GoogleLoginButton"
import { AuthDivider } from "@/components/auth/AuthDivider"
import { PasswordStrengthMeter } from "@/components/auth/PasswordStrengthMeter"
import { Eye, EyeOff } from "lucide-react"
export function AuthForm() {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { signIn, signUp } = useAuth()
  const [isSuccess, setIsSuccess] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [resetEmail, setResetEmail] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [capsLockOn, setCapsLockOn] = useState(false)

  // Detect caps lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.getModifierState && e.getModifierState('CapsLock')) {
        setCapsLockOn(true)
      } else {
        setCapsLockOn(false)
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.getModifierState && e.getModifierState('CapsLock')) {
        setCapsLockOn(true)
      } else {
        setCapsLockOn(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  async function handlePasswordReset(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSuccess(false)
    setIsLoading(true)

    try {
      const { supabase } = await import("@/lib/supabase")
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })

      if (error) throw error

      setIsSuccess(true)
      setResetEmail("")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send reset email"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSuccess(false)
    setIsLoading(true)

    try {
      const { supabase } = await import("@/lib/supabase")
      const { error } = await supabase.auth.signInWithOtp({
        email: resetEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      })

      if (error) throw error

      setIsSuccess(true)
      setResetEmail("")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send magic link"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSuccess(false)
    setIsLoading(true)

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          throw new Error("Passwords don't match")
        }
        await signUp(email, password)
        setIsSuccess(true)
        setIsLoading(false)
        return
      }

      await signIn(email, password)
      // Navigation will be handled by AuthProvider
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed"
      setError(message.includes("Email not confirmed") 
        ? "Please check your email to confirm your account" 
        : message)
    } finally {
      setIsLoading(false)
    }
  }

  // Reset states when switching modes
  const handleModeSwitch = (mode: 'signin' | 'signup' | 'reset' | 'magic') => {
    setError(null)
    setIsSuccess(false)
    if (mode === 'reset') {
      setShowPasswordReset(true)
      setShowMagicLink(false)
      setIsSignUp(false)
    } else if (mode === 'magic') {
      setShowMagicLink(true)
      setShowPasswordReset(false)
      setIsSignUp(false)
    } else if (mode === 'signup') {
      setShowPasswordReset(false)
      setShowMagicLink(false)
      setIsSignUp(true)
    } else {
      setShowPasswordReset(false)
      setShowMagicLink(false)
      setIsSignUp(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Glassmorphism container */}
      <div className="rounded-3xl bg-white/5 backdrop-blur-lg border border-white/10 p-8 shadow-xl">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">
            {showPasswordReset ? "Reset Password" : showMagicLink ? "Magic Link" : isSignUp ? "Create Account" : "Sign In"}
          </h1>
          <p className="text-sm text-white/60">
            {showPasswordReset 
              ? "Enter your email to receive a password reset link"
              : showMagicLink
              ? "Enter your email to receive a sign-in link"
              : isSignUp 
              ? "Enter your details to create a new account"
              : "Enter your email and password to access your account"
            }
          </p>
        </div>

        {/* Password Reset / Magic Link Form */}
        {(showPasswordReset || showMagicLink) && (
          <form onSubmit={showPasswordReset ? handlePasswordReset : handleMagicLink} className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="resetEmail" className="text-white/90 text-sm">Email</Label>
              <Input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4"
              />
            </div>
            {error && (
              <div className="rounded-full bg-red-500/10 border border-red-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}
            {isSuccess && (
              <div className="rounded-full bg-green-500/10 border border-green-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-green-300">
                  {showPasswordReset ? "Reset link sent! Check your email" : "Magic link sent! Check your email"}
                </p>
              </div>
            )}
            <Button
              type="submit"
              className="w-full rounded-full h-11 bg-primary hover:bg-primary/90 font-semibold"
              disabled={isLoading}
            >
              {isLoading 
                ? "Sending..." 
                : (showPasswordReset ? "Send Reset Link" : "Send Magic Link")
              }
            </Button>
            <button
              type="button"
              onClick={() => handleModeSwitch('signin')}
              className="w-full text-sm text-white/60 hover:text-white/90 transition-colors"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {/* Normal Sign In / Sign Up Form */}
        {!showPasswordReset && !showMagicLink && (
          <form onSubmit={onSubmit} className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/90 text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-white/90 text-sm">Password</Label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => handleModeSwitch('reset')}
                    className="text-xs text-white/60 hover:text-white/90 transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/90 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {capsLockOn && (
                <p className="text-xs text-yellow-300 flex items-center gap-1">
                  <span>⚠️</span> Caps Lock is on
                </p>
              )}
              {isSignUp && password && (
                <PasswordStrengthMeter password={password} email={email} />
              )}
            </div>
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/90 text-sm">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/90 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
            {error && (
              <div className="rounded-full bg-red-500/10 border border-red-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}
            {isSuccess && (
              <div className="rounded-full bg-green-500/10 border border-green-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-green-300">
                  Check your email to confirm your account
                </p>
              </div>
            )}
            <Button
              type="submit"
              className="w-full rounded-full h-11 bg-primary hover:bg-primary/90 font-semibold"
              disabled={isLoading}
            >
              {isLoading 
                ? (isSignUp ? "Creating account..." : "Signing in...") 
                : (isSignUp ? "Create Account" : "Sign In")
              }
            </Button>

            {/* Magic Link Option */}
            {!isSignUp && (
              <button
                type="button"
                onClick={() => handleModeSwitch('magic')}
                className="w-full text-sm text-white/60 hover:text-white/90 transition-colors"
              >
                Or sign in with magic link →
              </button>
            )}
          </form>
        )}

        <AuthDivider />
        
        {/* OAuth Buttons */}
        <div className="mb-6">
          <GoogleLoginButton />
        </div>

        {/* Toggle Sign In/Sign Up */}
        {!showPasswordReset && !showMagicLink && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => handleModeSwitch(isSignUp ? 'signin' : 'signup')}
              className="text-sm text-white/60 hover:text-white/90 transition-colors underline-offset-4 hover:underline"
            >
              {isSignUp 
                ? "Already have an account? Sign in" 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
